// KPI charts, drawn as SVG on the page's color tokens: paid seats against the goal line, and bugs per week.
import { addDays, daysBetween, type KpiGoals, type KpiResponse } from '@upm/domain';
import { fmtCalendarDate } from '../../lib/format';

const INK = 'rgb(var(--muted))';
const GRID = 'rgb(var(--line-soft))';
const BRAND = 'rgb(var(--brand))';
const SOFT = 'rgb(143 160 200)';
const BLUE = 'rgb(var(--info-dot))';
const FADED = 'rgb(var(--line-strong))';

const W = 640;
const L = 40;       // left edge of the plot
const R = 620;      // right edge
const TOP = 12;
const BASE = 172;   // the zero line
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const STEPS = [1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000];

/** Whole-number gridlines: the smallest round step that covers v in at most five of them. */
export function axis(v: number): { max: number; step: number } {
  const step = STEPS.find((s) => Math.ceil(v / s) <= 5) ?? Math.ceil(v / 5000) * 1000;
  return { max: step * Math.max(1, Math.ceil(v / step)), step };
}
const gridlines = (max: number, step: number) => Array.from({ length: Math.round(max / step) + 1 }, (_, i) => i * step);

/** First days of the months inside [from, to], thinned to at most `max` labels. */
function monthTicks(from: string, to: string, max = 8): Array<{ on: string; label: string }> {
  const out: Array<{ on: string; label: string }> = [];
  let [y, m] = from.split('-').map(Number) as [number, number];
  for (;;) {
    m += 1;
    if (m > 12) { m = 1; y += 1; }
    const on = `${y}-${String(m).padStart(2, '0')}-01`;
    if (on > to) break;
    out.push({ on, label: m === 1 ? `${MONTHS[0]} ${y}` : MONTHS[m - 1]! });
  }
  const every = Math.ceil(out.length / max) || 1;
  return out.filter((_, i) => i % every === 0);
}

export function SeatsChart({ seats, goal, today }: { seats: KpiResponse['seats']; goal: KpiGoals['paidSeats']; today: string }) {
  const from = goal?.startOn ?? seats.series[0]?.on ?? today;
  const to = goal?.targetOn ?? addDays(from > today ? from : today, 30);
  const span = Math.max(1, daysBetween(from, to));
  const x = (on: string) => L + (Math.min(Math.max(daysBetween(from, on), 0), span) / span) * (R - L);
  const { max, step } = axis(Math.max(goal?.target ?? 0, seats.projectedAtTarget ?? 0, ...seats.series.map((p) => p.total), 4));
  const y = (n: number) => BASE - (n / max) * (BASE - TOP);

  // Seats stay the same between changes, so the line steps: flat to the next change, then up or down.
  const pts = seats.series.filter((p) => p.on >= from);
  const path = pts.map((p, i) => `${i === 0 ? 'M' : `L${x(p.on)},${y(pts[i - 1]!.total)} L`}${x(p.on)},${y(p.total)}`).join(' ');
  const ticks = gridlines(max, step);
  const label = goal
    ? `Paid seats are ${seats.paid} today against a goal of ${goal.target} by ${fmtCalendarDate(goal.targetOn)}.`
    : `Paid seats are ${seats.paid} today. No goal is set.`;
  const labelRight = x(today) > L + (R - L) * 0.6;

  return (
    <svg viewBox={`0 0 ${W} 222`} width="100%" role="img" aria-label={label} style={{ display: 'block', font: '12px Inter, system-ui, sans-serif' }}>
      {ticks.map((n) => (
        <g key={n}>
          <line x1={L} x2={R} y1={y(n)} y2={y(n)} stroke={GRID} />
          <text x={L - 6} y={y(n) + 4} textAnchor="end" fill={INK}>{n}</text>
        </g>
      ))}
      {monthTicks(from, to).map((t) => <text key={t.on} x={x(t.on)} y={BASE + 18} textAnchor="middle" fill={INK}>{t.label}</text>)}
      {goal && (
        <>
          <line x1={x(goal.startOn)} y1={y(0)} x2={x(goal.targetOn)} y2={y(goal.target)} stroke={FADED} strokeWidth={1.5} strokeDasharray="5 4" />
          <text x={R} y={Math.min(y(goal.target), y(Math.max(seats.projectedAtTarget ?? 0, goal.target))) - 8} textAnchor="end" fill={INK}>Goal {goal.target}</text>
          {seats.projectedAtTarget !== null && today < goal.targetOn && (
            <>
              <line x1={x(today)} y1={y(seats.paid)} x2={x(goal.targetOn)} y2={y(seats.projectedAtTarget)} stroke={BLUE} strokeWidth={2} strokeDasharray="2 4" />
              <text x={R} y={y(max * 0.28)} textAnchor="end" fill={INK}>At the current rate: {seats.projectedAtTarget} by {fmtCalendarDate(goal.targetOn)}</text>
            </>
          )}
        </>
      )}
      {path && <path d={path} fill="none" stroke={BRAND} strokeWidth={2.5} strokeLinejoin="round" />}
      <circle cx={x(today)} cy={y(seats.paid)} r={5} fill={BRAND} />
      <text x={x(today) + (labelRight ? -9 : 9)} y={y(seats.paid) - 10} textAnchor={labelRight ? 'end' : 'start'} fill="rgb(var(--ink))" fontWeight={700}>{seats.paid} today</text>
      <g transform={`translate(${L} 214)`}>
        <line x1={0} x2={22} y1={-4} y2={-4} stroke={BRAND} strokeWidth={2.5} /><text x={28} y={0} fill={INK}>Paid seats</text>
        {goal && <><line x1={110} x2={132} y1={-4} y2={-4} stroke={FADED} strokeWidth={1.5} strokeDasharray="5 4" /><text x={138} y={0} fill={INK}>Goal pace</text></>}
        {goal && <><line x1={220} x2={242} y1={-4} y2={-4} stroke={BLUE} strokeWidth={2} strokeDasharray="2 4" /><text x={248} y={0} fill={INK}>Current rate</text></>}
      </g>
    </svg>
  );
}

export function BugsChart({ weeks }: { weeks: KpiResponse['bugs']['weeks'] }) {
  const n = weeks.length;
  const { max, step } = axis(Math.max(...weeks.map((w) => w.count), 4));
  const y = (v: number) => BASE - (v / max) * (BASE - TOP);
  const slot = (R - L) / n;
  const barW = Math.min(36, slot - 6);
  const every = n <= 12 ? 3 : 5;
  const total = weeks.reduce((a, w) => a + w.count, 0);
  const label = `${total} bugs submitted over the last ${n} weeks. The most recent full week had ${weeks.at(-1)?.count ?? 0}.`;
  return (
    <svg viewBox={`0 0 ${W} 222`} width="100%" role="img" aria-label={label} style={{ display: 'block', font: '12px Inter, system-ui, sans-serif' }}>
      {gridlines(max, step).map((v) => (
        <g key={v}>
          <line x1={L} x2={R} y1={y(v)} y2={y(v)} stroke={GRID} />
          <text x={L - 6} y={y(v) + 4} textAnchor="end" fill={INK}>{v}</text>
        </g>
      ))}
      {weeks.map((w, i) => {
        const cx = L + slot * i + slot / 2;
        const last = i === n - 1;
        return (
          <g key={w.start}>
            <rect x={cx - barW / 2} y={y(w.count)} width={barW} height={BASE - y(w.count)} rx={4} fill={last ? BRAND : SOFT} />
            {(n <= 12 || last) && <text x={cx} y={y(w.count) - 5} textAnchor="middle" fill="rgb(var(--ink))" fontWeight={600}>{w.count}</text>}
            {(i % every === 0 || last) && <text x={cx} y={BASE + 18} textAnchor="middle" fill={INK}>{fmtCalendarDate(w.start).replace(/, \d{4}$/, '')}</text>}
          </g>
        );
      })}
      <text x={L} y={214} fill={INK}>Week starting Monday · bug reports from live firms</text>
    </svg>
  );
}
